import { formatPointsAsUnit, getUnitSuffix } from '@/lib/ruler/format-unit'
import type { computeSinglePageHorizontalGuideSpan } from '../helpers/spread-geometry'
import type { CenterCanvasConfig } from '../types/center-canvas-config'
import type { DraggingGuide } from '../types/dragging-guide'

/**
 * Dragging guide preview (ruler drag to create) + live X/Y readout, drawn in canvas space as a
 * device-pixel hairline. On single-first/single-last a horizontal preview spans the whole spread
 * (`singlePageHorizontalGuideSpan`), like the committed guides.
 */
export function GuideCreatePreview({
  draggingGuide,
  scale,
  rulerUnit,
  snappedSpreadOriginCanvasX,
  verticalRulerZeroAtCanvasY,
  singlePageHorizontalGuideSpan,
}: {
  draggingGuide: DraggingGuide
  scale: number
  rulerUnit: CenterCanvasConfig['rulerUnit']
  snappedSpreadOriginCanvasX: number
  verticalRulerZeroAtCanvasY: number
  singlePageHorizontalGuideSpan: ReturnType<typeof computeSinglePageHorizontalGuideSpan>
}) {
  const isH = draggingGuide.type === 'horizontal'
  const pos = draggingGuide.position
  const displayPos = isH ? pos - verticalRulerZeroAtCanvasY : pos - snappedSpreadOriginCanvasX
  const ppu =
    typeof window !== 'undefined' && scale > 0 ? scale * (window.devicePixelRatio || 1) : 1
  const hairline = 1 / ppu
  const snapped = typeof window !== 'undefined' && scale > 0 ? Math.round(pos * ppu) / ppu : pos
  const spanStyle =
    isH && singlePageHorizontalGuideSpan
      ? {
          left: singlePageHorizontalGuideSpan.left,
          width: singlePageHorizontalGuideSpan.width,
          right: 'auto' as const,
        }
      : isH
        ? { left: 0, right: 0 }
        : { top: 0, bottom: 0 }

  return (
    <div
      className="absolute pointer-events-none"
      style={{
        zIndex: 31,
        ...spanStyle,
        ...(isH ? { top: snapped, height: hairline } : { left: snapped, width: hairline }),
      }}
    >
      <div
        className="doc-guide-line absolute"
        style={
          isH
            ? { top: 0, left: 0, right: 0, height: hairline }
            : { left: 0, top: 0, bottom: 0, width: hairline }
        }
      />
      <div
        className="absolute bg-page-editor-ruler-guide text-white text-[10px] font-semibold px-1.5 py-0.5 rounded whitespace-nowrap font-mono"
        style={isH ? { top: -20, left: 2 } : { top: 2, left: 8 }}
      >
        {isH ? 'Y' : 'X'}:{' '}
        {rulerUnit
          ? `${formatPointsAsUnit(displayPos, rulerUnit)} ${getUnitSuffix(rulerUnit)}`
          : `${Math.round(displayPos)}px`}
      </div>
    </div>
  )
}
